import React, { useState } from 'react';
import { Table, Button, Space, Modal, Card, Typography, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { DynamicFormListProps, FormData, FormFieldConfig } from './types';

const { Title } = Typography;
const { confirm } = Modal;

/**
 * 动态表单列表组件
 * 用于显示和管理表单数据
 */
const DynamicFormList: React.FC<DynamicFormListProps> = ({
  config,
  dataSource,
  loading = false,
  onAdd,
  onEdit,
  onDelete,
  onView
}) => {
  // 生成表格列
  const generateColumns = () => {
    // 从配置中获取字段作为列
    const fieldColumns = config.fields.map(field => ({
      title: field.label,
      dataIndex: ['data', field.key],
      key: field.key,
      ellipsis: true,
      render: (text: any, record: FormData) => renderCellContent(field, record.data[field.key])
    }));

    // 添加操作列
    const actionColumn = {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: any, record: FormData) => (
        <Space size="small">
          {onView && (
            <Tooltip title="查看">
              <Button 
                type="text" 
                icon={<EyeOutlined />} 
                onClick={() => onView(record)} 
              />
            </Tooltip>
          )}
          {onEdit && (
            <Tooltip title="编辑">
              <Button 
                type="text" 
                icon={<EditOutlined />} 
                onClick={() => onEdit(record)} 
              />
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="删除">
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />} 
                onClick={() => handleDelete(record)} 
              />
            </Tooltip>
          )}
        </Space>
      )
    };

    return [...fieldColumns, actionColumn];
  };

  // 渲染单元格内容
  const renderCellContent = (field: FormFieldConfig, value: any) => {
    if (value === undefined || value === null) {
      return '-';
    }

    switch (field.type) {
      case 'select':
      case 'radio':
      case 'checkbox':
        // 对于选择类型，显示选项的标签而不是值
        if (Array.isArray(value)) {
          // 多选情况
          const selectedLabels = field.options
            ?.filter(option => value.includes(option.value))
            .map(option => option.label);
          return selectedLabels?.join(', ') || value.join(', ');
        } else {
          // 单选情况
          const option = field.options?.find(opt => opt.value === value);
          return option?.label || value;
        }
      
      case 'switch':
        return value ? '是' : '否';
      
      case 'date':
        // 日期格式化
        return value ? new Date(value).toLocaleDateString() : '-';
      
      default:
        return value;
    }
  };

  // 处理删除
  const handleDelete = (record: FormData) => {
    if (!onDelete || !record.id) return;

    confirm({
      title: '确认删除',
      content: '确定要删除这条记录吗？此操作不可恢复。',
      okText: '确认',
      okType: 'danger',
      cancelText: '取消',
      onOk() {
        onDelete(record.id as string);
      }
    });
  };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4}>{config.name}列表</Title>
        {onAdd && (
          <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>
            添加
          </Button>
        )}
      </div>

      <Table
        rowKey={record => record.id || ''}
        columns={generateColumns()}
        dataSource={dataSource}
        loading={loading}
        pagination={{ 
          showSizeChanger: true, 
          showTotal: total => `共 ${total} 条记录`,
          defaultPageSize: 10
        }}
      />
    </Card>
  );
};

export default DynamicFormList; 